import { useId, forwardRef, useState } from "react";
import { type TextAreaProps } from "./types";
import { VStack } from "../../primitives/stack";
import { Text } from "../../primitives/text";
import { useTheme } from "@repo/tokens";
import { resolveThemeToken } from "../../utils/theme";

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      value,
      defaultValue,
      onChangeText,
      label,
      placeholder,
      helperText,
      errorMessage,
      isDisabled,
      isReadOnly,
      isInvalid,
      size = "md",
      variant = "outline",
      width = "100%",
      id: providedId,
      rows = 3,
      resize = "vertical",
      style,
      borderRadius,
      borderColor: borderColorProp,
      placeholderColor: placeholderColorProp,
      labelColor: labelColorProp,
      ...props
    },
    ref,
  ) => {
    const { theme } = useTheme();
    const generatedId = useId();
    const inputId = providedId || generatedId;
    const errorId = `${inputId}-error`;
    const helperId = `${inputId}-helper`;

    const [isFocused, setIsFocused] = useState(false);

    // Token resolution
    const bg = resolveThemeToken(
      theme,
      isDisabled ? "input.background.disabled" : "input.background.default",
    );

    let borderColorToken = "input.border.default";
    if (isInvalid) borderColorToken = "input.border.error";
    else if (isFocused) borderColorToken = "input.border.focus";

    const themeBorderColor = resolveThemeToken(theme, borderColorToken);
    const borderColor =
      !isInvalid && !isFocused && borderColorProp
        ? borderColorProp
        : themeBorderColor;
    const radius = borderRadius
      ? typeof borderRadius === "number"
        ? `${borderRadius}px`
        : borderRadius
      : resolveThemeToken(theme, "input.radius");
    const paddingValue =
      resolveThemeToken(theme, `textarea.padding.${size}`) ||
      resolveThemeToken(theme, `input.padding.${size}`);
    const padding =
      typeof paddingValue === "number"
        ? `${paddingValue / 16}rem`
        : paddingValue;
    const labelOffset = variant === "floating" ? "0.5rem" : 0;
    const resolvedPlaceholderColor =
      placeholderColorProp ?? resolveThemeToken(theme, "text.placeholder");
    const resolvedLabelColor =
      labelColorProp ?? resolveThemeToken(theme, "text.secondary");
    const placeholderClassName = `${inputId.replace(/[^a-zA-Z0-9_-]/g, "-")}-placeholder`;

    return (
      <VStack gap="xs" style={{ width }}>
        <style>
          {`.${placeholderClassName}::placeholder { color: ${resolvedPlaceholderColor}; opacity: 1; }`}
        </style>
        {label && variant !== "floating" && (
          <Text
            as="label"
            htmlFor={inputId}
            variant="body"
            style={{ fontWeight: 500 }}
          >
            {label}
          </Text>
        )}

        <div
          style={{
            backgroundColor: bg,
            border:
              variant === "outline" || variant === "floating"
                ? `1px solid ${borderColor}`
                : "none",
            borderBottom:
              variant === "flushed" || variant === "floating"
                ? `1px solid ${borderColor}`
                : undefined,
            borderRadius:
              variant === "outline" || variant === "floating" ? radius : 0,
            padding: 0,
            opacity: isDisabled ? 0.6 : 1,
            cursor: isDisabled ? "not-allowed" : "text",
            transition: "border-color 0.2s ease",
            display: "flex",
            position: "relative",
            marginTop: labelOffset,
            width: "100%",
          }}
        >
          {label && variant === "floating" && (
            <Text
              as="label"
              htmlFor={inputId}
              variant="body"
              style={
                {
                  position: "absolute",
                  left: 0,
                  top: 0,
                  paddingLeft: 4,
                  paddingRight: 4,
                  transform:
                    isFocused ||
                    (value !== undefined && value !== null && value !== "") ||
                    (defaultValue !== undefined &&
                      defaultValue !== null &&
                      defaultValue !== "")
                      ? `translate(0.875rem, -0.5625rem) scale(0.75)`
                      : `translate(0.875rem, 1rem) scale(1)`,
                  transformOrigin: "top left",
                  backgroundColor:
                    isFocused ||
                    (value !== undefined && value !== null && value !== "") ||
                    (defaultValue !== undefined &&
                      defaultValue !== null &&
                      defaultValue !== "")
                      ? resolveThemeToken(theme, "surface.card")
                      : "transparent",
                  fontSize: 16,
                  color: isInvalid
                    ? resolveThemeToken(theme, "feedback.error")
                    : resolvedLabelColor,
                  transition:
                    "color 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms, transform 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms",
                  pointerEvents: "none",
                  zIndex: 1,
                  fontWeight:
                    isFocused ||
                    (value !== undefined && value !== null && value !== "") ||
                    (defaultValue !== undefined &&
                      defaultValue !== null &&
                      defaultValue !== "")
                      ? 500
                      : 400,
                  lineHeight: "1.5rem",
                  letterSpacing: "0.0313rem",
                } as React.CSSProperties
              }
            >
              {label}
            </Text>
          )}

          <textarea
            ref={ref}
            id={inputId}
            value={value}
            defaultValue={defaultValue}
            onChange={(e) => onChangeText?.(e.target.value)}
            disabled={isDisabled}
            readOnly={isReadOnly}
            placeholder={
              variant === "floating" && !isFocused ? "" : placeholder
            }
            rows={rows}
            onFocus={(e) => {
              setIsFocused(true);
              props.onFocus?.(e);
            }}
            onBlur={(e) => {
              setIsFocused(false);
              props.onBlur?.(e);
            }}
            aria-invalid={isInvalid}
            aria-describedby={
              errorMessage ? errorId : helperText ? helperId : undefined
            }
            className={placeholderClassName}
            style={{
              flex: 1,
              border: "none",
              outline: "none",
              background: "transparent",
              padding: padding,
              width: "100%",
              color: resolveThemeToken(theme, "text.primary"),
              resize: resize,
              fontFamily: "inherit",
              fontSize: "inherit",
              ...(style as React.CSSProperties),
            }}
            {...props}
          />
        </div>

        {errorMessage ? (
          <Text id={errorId} color="feedback.error" variant="caption">
            {errorMessage}
          </Text>
        ) : helperText ? (
          <Text id={helperId} color="text.secondary" variant="caption">
            {helperText}
          </Text>
        ) : null}
      </VStack>
    );
  },
);

TextArea.displayName = "TextArea";
